<script setup lang="ts">
import {defineExpose, onMounted, onUnmounted, ref, watch} from 'vue';

    const props = defineProps({
        size: { default: 'small', type: String },
        error: { default: null, type: [String, Boolean] },
        digits: { default: 2, type: Number },
        placeholder: { default: null, type: String },
        disabled: { default: false, type: Boolean },
        highlightOnFocus: { default: true, type: Boolean },
        fluid: { default: true, type: Boolean },
        suffix: { default: null, type: String },
        dropdown: { default: false, type: Boolean },
        dropdownOptions: { default: [], type: Array },
        max: { default: null, type: [null, Number] },
        nullable: { default: false, type: Boolean },
        class: { default: '', type: String }
    });

    const model = defineModel();

    model.value = model.value ? Number(model.value).toFixed(props.digits) : null;

    const ddOptions = ref([]);

    function searchDropdownOptions() {
        ddOptions.value = [...props.dropdownOptions];
    }

    const emits = defineEmits(['input', 'change', 'plus', 'minus']);

    const inputValBeforeChange = ref();
    const changedInside = ref(false);
    const inputRef = ref();
    const canListenToKeydown= ref(true);

    // Function to convert Arabic numerals (٠-٩) to English (0-9)
    const convertArabicToEnglish = (str) => {
        return str.replace(/[\u0660-\u0669]/g, (c) => c.charCodeAt(0) - 0x0660);
    };

    const handleKeydown = (event) => {
        if (event.key === 'Unidentified') {
            canListenToKeydown.value = false;
        }

        const allowedKeys = ['ArrowLeft', 'ArrowRight', 'Tab'];
        const isArabicNumber = /[\u0660-\u0669]/.test(event.key);
        const isEnglishNumber = /[0-9]/.test(event.key);
        const isDot = event.key === '.'; // Allow dot (.)


        if (allowedKeys.includes(event.key) ||
            ((event.ctrlKey || event.metaKey) && ['a', 'c', 'v'].includes(event.key)) // Allow Select All, Copy, and Paste
        ) {
            return;
        }

        const inputElement = event.target;

        // Handle Backspace & Delete properly
        if (event.key === 'Backspace' || event.key === 'Delete') {
            event.preventDefault();

            handleDelete(inputElement, event.key === 'Backspace');

            return;
        }

        if (event.key === 'Enter') {
            event.preventDefault();

            if (inputElement.value.length === 0) {
                model.value = props.nullable ? null : 0;

                emits('input', props.nullable ? null : 0);
            }

            focusOut(true);

            const inputs = Array.from(document.querySelectorAll('input[type=\'text\'], textarea'));
            const currentIndex = inputs.indexOf(event.target);

            if (currentIndex !== -1 && currentIndex < inputs.length - 1) {
                setTimeout(() => {
                    inputs[currentIndex + 1].focus(); // Move to next text field
                });
            }

            return;
        }

        if (event.key === '+') {
            event.preventDefault();

            emits('plus');

            return;
        }

        if (event.key === '-') {
            event.preventDefault();

            emits('minus');

            return;
        }

        const start = inputElement.selectionStart;
        const end = inputElement.selectionEnd;

        // Prevent multiple dots
        if (props.digits > 0 && isDot && inputElement.value.includes('.')) {
            event.preventDefault();
            return;
        }

        if (isArabicNumber || isEnglishNumber || (props.digits > 0 && isDot)) {
            event.preventDefault(); // Prevent the default behavior

            const convertedChar = isArabicNumber ? convertArabicToEnglish(event.key) : event.key;

            // Replace selected text (if any) with the new character
            let newValue = inputElement.value.substring(0, start) + convertedChar + inputElement.value.substring(end);

            // Ensure only allowed decimal places
            const dotIndex = newValue.indexOf('.');
            if (dotIndex !== -1 && newValue.length - dotIndex - 1 > props.digits) {
                return; // Stop if more decimal places than allowed
            }

            // Prevent exceeding max number
            if (props.max && parseFloat(newValue) > props.max) {
                return;
            }

            updateInputValue(newValue);
        } else {
            event.preventDefault(); // Block invalid characters
        }
    };

    const handlePaste = (event) => {
        const pastedText = event.clipboardData.getData('text');
        const convertedText = convertArabicToEnglish(pastedText);

        event.preventDefault();

        const inputElement = event.target;
        const start = inputElement.selectionStart;
        const end = inputElement.selectionEnd;

        let newValue = inputElement.value.substring(0, start) + convertedText + inputElement.value.substring(end);

        // Prevent exceeding max number
        if (props.max && parseFloat(newValue) > props.max) {
            newValue = props.max;
        }

        updateInputValue(newValue);
    };

    const handleDelete = (inputElement, isBackspace) => {
        const start = inputElement.selectionStart;
        const end = inputElement.selectionEnd;
        let newValue = inputElement.value;

        if (start === end) {
            if (isBackspace && start > 0) {
                newValue = newValue.slice(0, start - 1) + newValue.slice(start);
            } else if (!isBackspace && start < newValue.length) {
                newValue = newValue.slice(0, start) + newValue.slice(start + 1);
            }
        } else {
            newValue = newValue.slice(0, start) + newValue.slice(end);
        }

        updateInputValue(newValue);
    };

    const updateInputValue = (newValue: any) => {
        // Update the model value
        model.value = props.nullable && !newValue ? null : newValue;

        emits('input', props.nullable && !newValue ? null : Number(newValue));
    };

    const changeInputValue = (value: number|null) => {
        emits('change', value);

        // emit input event if the component can not listening to the keydown event
        if (!canListenToKeydown.value) {
            emits('input', value);
        }
    }

    function focusOut(inside = false) {
        model.value = props.nullable && !model.value ? null : Number(model.value || 0).toFixed(props.digits);

        if ((typeof inside == 'boolean' && inside) || Number(model.value) !== Number(inputValBeforeChange.value)) {
            changeInputValue(props.nullable && !model.value ? null : Number(model.value));
        }

        changedInside.value = typeof inside == 'boolean' ? inside : false;
    }

    function focusIn(e) {
        props.highlightOnFocus && e.target.select();

        inputValBeforeChange.value = model.value;

        changedInside.value = true;
    }

    function dropdownOptionSelected({ value }) {
        changeInputValue(Number(value))
    }

    watch(model, (newValue, oldValue) => {
        if (!isNaN(newValue) && newValue !== null && Number(newValue) >= 0 && !changedInside.value) {
            const newV = Number(newValue).toFixed(props.digits);
            if (oldValue !== newValue) {
                model.value = newV;
            }

            const oldV = Number(oldValue).toFixed(props.digits);
            if (newV !== oldV) {
                changeInputValue(props.nullable && !newV ? null : Number(newV));
            }
        }
    });

    watch(() => props.max, () => {
        if (model.value && props.max && Number(model.value) > props.max) {
            updateInputValue(props.max);
        }
    });

    const focus = () => {
        inputRef.value?.$el?.focus();
    };

    defineExpose({
        focus
    });
</script>

<template>
    <template v-if="props.dropdown">
        <AutoComplete
            v-model="model"
            dropdown
            :suggestions="ddOptions"
            @complete="searchDropdownOptions"
            @keydown="handleKeydown"
            @paste="handlePaste"
            @blur="focusOut"
            @focus="focusIn"
            @optionSelect="dropdownOptionSelected"
            v-keyfilter.num
            :size="props.size"
            :invalid="!!error"
            :placeholder="placeholder"
            :disabled="disabled"
            :fluid="props.fluid"
            ref="inputRef"
        />
    </template>
    <template v-else-if="props.suffix">
        <InputGroup>
            <InputText
                @keydown="handleKeydown"
                @paste="handlePaste"
                @focusout="focusOut"
                @focus="focusIn"
                v-keyfilter.num
                v-model="model"
                :size="props.size"
                :invalid="!!error"
                :placeholder="placeholder"
                :disabled="disabled"
                :fluid="props.fluid"
                ref="inputRef"
            />
            <InputGroupAddon class="!bg-[#eee] text-xs !text-black">
                {{ props.suffix }}
            </InputGroupAddon>
        </InputGroup>
    </template>
    <template v-else>
        <InputText
            ref="inputRef"
            @keydown="handleKeydown"
            @paste="handlePaste"
            @focusout="focusOut"
            @focus="focusIn"
            v-keyfilter.num
            v-model="model"
            :size="props.size"
            :invalid="!!error"
            :placeholder="placeholder"
            :disabled="disabled"
            :fluid="props.fluid"
            :class="props.class"
        />
    </template>
    <Message v-if="!!props.error && props.error !== true" severity="error" :size="props.size" variant="simple">
        {{ props.error }}
    </Message>
</template>

<style scoped>

</style>
